import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Building2, Lock, ShieldAlert, ShieldCheck, UserCheck } from 'lucide-react';
import { AuthThemeToggle } from '../../components/AuthThemeToggle';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Card } from '../../components/Card';
import { Logo } from '../../components/Logo';
import { useBrand } from '../../brand/BrandProvider';
import { getInvite, acceptInvite, login, destinationForRole, isMfaChallenge } from '../../api/auth';

/**
 * Accept invite (set password) — §4.3 / §6.3
 * Lands here from the invitation email link (/accept-invite?token=…). Pulls the
 * invite context from the server-side token; on success it sets the password,
 * creates/attaches the account + role, and signs the user straight in.
 */
export function AcceptInvite() {
  const brand = useBrand();
  const navigate = useNavigate();
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token') ?? '', []);

  const { data: invite, isLoading, isError } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => getInvite(token),
    enabled: !!token,
    retry: false,
  });

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;
  const valid = !tooShort && !mismatch && password.length >= 8;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid || !invite) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await acceptInvite(token, { name: invite.name ?? undefined, password });
      if (res.passwordSet) {
        // New / passwordless account — sign in with the password just set, then
        // route to the shell that matches the granted role.
        try {
          const outcome = await login(res.email, password);
          // A brand-new account cannot have 2FA yet, but the type forces the
          // check — and if one ever did, sending them to /login is correct:
          // that page owns the code step.
          if (isMfaChallenge(outcome)) {
            navigate({ to: '/login' });
            return;
          }
          navigate({ to: destinationForRole(outcome.user.role) });
          return;
        } catch {
          navigate({ to: '/login' });
          return;
        }
      }
      // Account already existed with a password — the role was added; go sign in.
      navigate({ to: '/login' });
    } catch {
      setSubmitError('This invitation is no longer valid. Ask whoever invited you to resend it.');
      setSubmitting(false);
    }
  }

  const tokenMissing = !token;
  const invalid = !isLoading && (tokenMissing || isError || (invite && !invite.valid));

  return (
    <div className="min-h-screen bg-marketing dark:bg-marketing-dark relative flex items-center justify-center px-4 py-10">
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
        <AuthThemeToggle />
      </div>
      <div className="w-full max-w-md">
        {isLoading ? (
          <Card className="p-7 text-center text-sm text-zinc-600 dark:text-zinc-300">Loading your invitation…</Card>
        ) : invalid ? (
          <InvalidState
            expired={!!invite?.expired}
            used={invite?.status === 'accepted'}
            onSignIn={() => navigate({ to: '/login' })}
          />
        ) : invite ? (
          <>
            <div className="flex flex-col items-center mb-8">
              <Logo className="w-10 h-10 rounded-xl" />
              <h1 className="mt-4 text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 text-center">
                Welcome{invite.name ? `, ${invite.name.split(' ')[0]}` : ''}
              </h1>
              <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300 text-center max-w-sm">
                {invite.inviterName ?? 'An administrator'} has invited you to join{' '}
                <span className="font-medium text-zinc-900 dark:text-zinc-100">
                  {invite.orgName ?? 'the team'}
                </span>
                {invite.scope === 'business' ? "'s POS." : '.'}
              </p>
            </div>

            <Card className="p-5 sm:p-7">
              <div className="rounded-lg border border-zinc-200 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-800/40 px-4 py-3 mb-5">
                <dl className="space-y-2 text-sm">
                  <Row
                    icon={<Building2 className="w-3.5 h-3.5" />}
                    label={invite.scope === 'business' ? 'Business' : 'Organization'}
                    value={invite.orgName ?? brand.name}
                  />
                  <Row
                    icon={<UserCheck className="w-3.5 h-3.5" />}
                    label="Your role"
                    value={
                      <span className="inline-flex items-center gap-1.5">
                        {invite.roleLabel}
                        {invite.posOnly && (
                          <span className="px-1.5 py-px rounded text-[10px] font-medium bg-brand-subtle text-brand dark:bg-brand-dm-subtle dark:text-brand-dm">
                            POS only
                          </span>
                        )}
                      </span>
                    }
                  />
                  <Row
                    icon={<Lock className="w-3.5 h-3.5" />}
                    label="Account email"
                    value={<span className="mono text-xs">{invite.email}</span>}
                  />
                </dl>
              </div>

              <form onSubmit={onSubmit} className="space-y-5">
                <Input
                  label="Choose a password"
                  type="password"
                  required
                  placeholder="••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  hint="At least 8 characters with letters and numbers"
                  error={tooShort ? 'Must be at least 8 characters' : undefined}
                  autoComplete="new-password"
                />
                <Input
                  label="Confirm password"
                  type="password"
                  required
                  placeholder="••••••••••"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  error={mismatch ? "Passwords don't match" : undefined}
                  autoComplete="new-password"
                />

                {submitError && (
                  <p className="text-sm text-rose-600 dark:text-rose-400">{submitError}</p>
                )}

                <Button
                  type="submit"
                  fullWidth
                  disabled={!valid}
                  loading={submitting}
                  iconRight={!submitting ? <ArrowRight className="w-4 h-4" /> : undefined}
                >
                  {submitting ? 'Saving…' : 'Set password & continue'}
                </Button>

                <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400 justify-center">
                  <ShieldCheck className="w-3 h-3" />
                  <span>Invitation expires 48 hours after sending</span>
                </div>
              </form>
            </Card>
          </>
        ) : null}

        <p className="mt-8 text-center text-xs text-zinc-500 dark:text-zinc-400">
          Powered by{' '}
          <span className="font-semibold text-zinc-700 dark:text-zinc-200">{brand.name}</span>
        </p>
      </div>
    </div>
  );
}

function InvalidState({ expired, used, onSignIn }: { expired: boolean; used: boolean; onSignIn: () => void }) {
  const title = used ? 'Invitation already used' : expired ? 'Invitation expired' : 'Invitation not found';
  const body = used
    ? 'This invitation has already been accepted. Try signing in instead.'
    : expired
      ? 'This invitation has expired. Ask whoever invited you to send a fresh one.'
      : 'This invitation link is invalid. Check the link, or ask whoever invited you to resend it.';
  return (
    <Card className="p-7 text-center">
      <div className="mx-auto w-11 h-11 grid place-items-center rounded-full bg-amber-100 dark:bg-amber-500/15 mb-4">
        <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400" />
      </div>
      <h1 className="text-lg font-semibold text-zinc-950 dark:text-zinc-50">{title}</h1>
      <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">{body}</p>
      <Button className="mt-5" variant="secondary" fullWidth onClick={onSignIn}>
        Go to sign in
      </Button>
    </Card>
  );
}

function Row({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-5 grid place-items-center text-zinc-400 dark:text-zinc-500">
        {icon}
      </span>
      <dt className="text-zinc-500 dark:text-zinc-400 w-24 text-xs uppercase tracking-wide">
        {label}
      </dt>
      <dd className="flex-1 text-zinc-900 dark:text-zinc-100">{value}</dd>
    </div>
  );
}
